/* eslint-disable no-alert */
import {
  ApolloClient,
  ApolloLink,
  InMemoryCache,
  HttpLink,
} from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { Capacitor } from '@capacitor/core';

import {
  adminURL,
  apiURL,
  mode,
} from '@/common/config';
import { saveLastVisitedPage } from '@/common/helpers/restore/restore';
import { paths, publicPaths } from '@/routes/paths';

import { cacheConfig } from './apolloCache';
import { GlobalStateProvider } from '@/common/hooks/useGlobalState';
import { tokenIsValid } from '~/src/shared/utils/user';

export default function getApolloClient(history) {
  const cache = new InMemoryCache(cacheConfig);

  const hasErrorCode = (errors, code) => (errors?.graphQLErrors ?? []).some((error) => error.code === code);
  const errorLink = onError((error) => {
    if (hasErrorCode(error, 'UNAUTHORIZED')) {
      const { pathname } = window.location;
      if (pathname !== paths('token')) {
        saveLastVisitedPage();
        history.push(paths('token'));
      }
    } else if (hasErrorCode(error, 'SERVICE_UNAVAILABLE')) {
      mode.set('maintenance');
    } else if (hasErrorCode(error, 'EXPIRED')) {
      const globalState = GlobalStateProvider.current();
      window.alert('Impersonation session has expired. You will be redirected back to the admin page');

      try {
        const [impersonateId] = (globalState.state.impersonateId ?? '').split(':');
        const adminLink = `${adminURL}account/vetprofile/${impersonateId}/change/`;
        globalState.clear('impersonateId', 'impersonateUser');
        window.location.href = adminLink;
      } catch (e) {
        window.location.href = `${adminURL}account/vetprofile/`;
      }
    } else if (hasErrorCode(error, 'FORBIDDEN')) {
      window.alert('You can only view data when in an impersonation session');
    }
  });

  const authLink = new ApolloLink((operation, forward) => {
    if (operation.operationName === 'dynamicSettings') {
      return forward(operation);
    }

    const { userToken, impersonateId, qaAuthData } = GlobalStateProvider.current().state;
    const { pathname } = window.location;

    const headers = {};
    if (qaAuthData) {
      const [qaToken, qaTargetUser] = qaAuthData.split(':');
      headers['QA-Auth-Token'] = qaToken;
      headers['QA-Target-User'] = qaTargetUser;
    } else if (userToken) {
      try {
        if (tokenIsValid(userToken)) {
          headers.Authorization = `JWT ${userToken}`;
          headers['Vetco-Platform'] = Capacitor.getPlatform();
          headers['Vetco-App'] = 'DVMApp';
          if (impersonateId) {
            headers['Impersonate-User'] = impersonateId;
          }
        }
      } catch (error) {
        //
      }
    }

    if (Object.values(headers).length > 0) {
      operation.setContext({ headers });
    } else if (!publicPaths.includes(pathname)) {
      saveLastVisitedPage();
      if (userToken) {
        history.push(paths('token'));
      } else {
        history.push(paths('welcome'));
      }
    }

    return forward(operation);
  });

  return new ApolloClient({
    cache,
    resolvers: {},
    link: ApolloLink.from([
      authLink,
      errorLink,
      new HttpLink({
        uri: apiURL,
        fetch: (uri, options) => {
          const { operationName, query } = JSON.parse(options?.body);
          return fetch(`${uri}?${query[0]}=${operationName}`, options);
        },
      }),
    ]),
  });
}
